import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { call } from './api';

/** `GET /api/config` (www-server.md). The only source of service hosts and outbound links. */
export interface Hosts {
  auth: string;
  accounts: string;
  api: string;
  img: string;
  notify: string;
  rooms: string;
  cdn: string;
  storage: string;
  www: string;
  admin: string;
  /** optional: only some servers list it (profile subscriber count) */
  clubs: string;
}

export interface Links {
  discordInvite: string | null;
  privacyEmail: string | null;
  pcDownload: string | null;
  questDownload: string | null;
}

export interface SiteConfig {
  signupEnabled: boolean;
  turnstileSiteKey: string | null;
  benefitsEnabled: boolean;
  discordAuthorizeUrl: string | null;
  hosts: Hosts;
  links: Links;
  /** the config request failed: signup/benefits off, no game service reachable */
  failed: boolean;
}

const EMPTY_HOSTS: Hosts = {
  auth: '',
  accounts: '',
  api: '',
  img: '',
  notify: '',
  rooms: '',
  cdn: '',
  storage: '',
  www: '',
  admin: '',
  clubs: '',
};

const NO_LINKS: Links = {
  discordInvite: null,
  privacyEmail: null,
  pcDownload: null,
  questDownload: null,
};

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}

function trimSlash(s: string) {
  return s.replace(/\/+$/, '');
}

export function parseConfig(raw: Record<string, unknown>): SiteConfig {
  const h = (raw.hosts ?? {}) as Record<string, unknown>;
  const l = (raw.links ?? {}) as Record<string, unknown>;
  const hosts = { ...EMPTY_HOSTS };
  for (const k of Object.keys(EMPTY_HOSTS) as (keyof Hosts)[]) {
    const v = str(h[k]);
    if (v) hosts[k] = trimSlash(v);
  }
  return {
    signupEnabled: raw.signupEnabled === true,
    turnstileSiteKey: str(raw.turnstileSiteKey),
    benefitsEnabled: raw.benefitsEnabled === true,
    discordAuthorizeUrl: str(raw.discordAuthorizeUrl),
    hosts,
    links: {
      discordInvite: str(l.discordInvite),
      privacyEmail: str(l.privacyEmail),
      pcDownload: str(l.pcDownload),
      questDownload: str(l.questDownload),
    },
    failed: false,
  };
}

export const FAILED_CONFIG: SiteConfig = {
  signupEnabled: false,
  turnstileSiteKey: null,
  benefitsEnabled: false,
  discordAuthorizeUrl: null,
  hosts: EMPTY_HOSTS,
  links: NO_LINKS,
  failed: true,
};

const ConfigContext = createContext<SiteConfig | null>(null);

export function ConfigProvider({
  children,
  initial,
}: {
  children: ReactNode;
  /** tests pass a ready config */
  initial?: SiteConfig;
}) {
  const [config, setConfig] = useState<SiteConfig | null>(initial ?? null);
  useEffect(() => {
    if (initial) return;
    let live = true;
    call('/api/config')
      .then((raw) => live && setConfig(parseConfig(raw)))
      .catch(() => live && setConfig(FAILED_CONFIG));
    return () => {
      live = false;
    };
  }, [initial]);
  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
}

/** null while `/api/config` is loading. */
export function useConfigMaybe(): SiteConfig | null {
  return useContext(ConfigContext);
}

/** For components rendered only after the config has landed. */
export function useConfig(): SiteConfig {
  const c = useContext(ConfigContext);
  if (!c) throw new Error('useConfig used before the config loaded');
  return c;
}

/** Signup is open when it is enabled and a Turnstile site key came with it. */
export function signupOpen(c: SiteConfig) {
  return c.signupEnabled && c.turnstileSiteKey !== null;
}

export function isStaffHost(hostname: string) {
  return hostname.toLowerCase().startsWith('admin.');
}
